import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError, tokenStore } from "./api";
import { toast } from "sonner";
import { clearDistributorReview } from "./distributorReview";
import { clearAllStoredClientIds } from "./clientSelection";
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionRemember, setSessionRemember] = useState(() => tokenStore.isPersistent());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { timeout: 12000 });
        if (!cancelled) {
          setUser(data);
          setSessionRemember(tokenStore.isPersistent());
        }
      } catch {
        tokenStore.clear();
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      tokenStore.clear();
      clearDistributorReview();
      clearAllStoredClientIds();
      setUser(false);
      setSessionRemember(false);
    };
    window.addEventListener("visor:session-expired", onExpired);
    return () => window.removeEventListener("visor:session-expired", onExpired);
  }, []);

  const login = async ({ email, password, pin, remember }) => {
    setError("");
    const body = { email, remember: !!remember };
    if (pin) body.pin = pin;
    else if (password) body.password = password;
    let lastErr = null;
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { data } = await api.post("/auth/login", body, { timeout: 20000 });
        if (data.access_token) {
          tokenStore.set(data.access_token, data.refresh_token, { remember: !!remember });
        }
        setSessionRemember(!!remember);
        clearDistributorReview();
        setUser(data);
        return data;
      } catch (e) {
        lastErr = e;
        const status = e.response?.status;
        if (status && status !== 502 && status !== 503 && status !== 504) {
          const detail = e.response?.data?.detail;
          setError(formatApiError(detail) || e.message || "Credenciales inválidas");
          return null;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, (attempt + 1) * 1500));
        }
      }
    }
    const unreachable =
      !lastErr?.response &&
      (lastErr?.code === "ERR_NETWORK" ||
        lastErr?.code === "ECONNABORTED" ||
        /network|timeout|refused|empty/i.test(String(lastErr?.message || "")));
    setError(
      unreachable
        ? "No se pudo conectar con el servidor. Comprueba que la API esté en marcha (puerto 8000), recarga la página (Ctrl+Shift+R) o abre la aplicación en el navegador del sistema (Puertos → Abrir en el navegador)."
        : `El servidor no respondió a tiempo. Reintenta en unos segundos.${lastErr?.message ? ` (${lastErr.message})` : ""}`,
    );
    return null;
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      if (data.access_token) {
        tokenStore.set(data.access_token, data.refresh_token, { remember: false });
      }
      setSessionRemember(false);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const saasRegister = async (payload) => {
    setError("");
    try {
      tokenStore.clear();
      const { data } = await api.post("/auth/saas-register", payload);
      if (data.access_token) {
        tokenStore.set(data.access_token, data.refresh_token, { remember: false });
      }
      setSessionRemember(false);
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        is_admin_owner: data.is_admin_owner,
        is_buyer_tenant: data.is_buyer_tenant,
        tenant_buyer_id: data.tenant_buyer_id,
        portal_username: data.portal_username,
        needs_trial_registration: data.needs_trial_registration,
        trial_bootstrap: data.trial_bootstrap,
        trial_onboarding_completed_at: data.trial_onboarding_completed_at,
        can_generate_licenses: data.can_generate_licenses,
        can_switch_workspace: data.can_switch_workspace,
        has_global_client_supervision: data.has_global_client_supervision,
        scoped_workspace: data.scoped_workspace,
      });
      return data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      throw e;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    clearDistributorReview();
    clearAllStoredClientIds();
    setSessionRemember(false);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me", { timeout: 12000 });
      setUser(data);
      setSessionRemember(tokenStore.isPersistent());
      return data;
    } catch {
      tokenStore.clear();
      setSessionRemember(false);
      setUser(false);
      return null;
    }
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        error,
        sessionRemember,
        login,
        register,
        saasRegister,
        logout,
        setError,
        setUser,
        refreshUser,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
